/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import {BrowserModule} from '@angular/platform-browser';
import {NgModule, APP_INITIALIZER} from '@angular/core';
import {FormsModule} from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

import {AppRoutingModule} from './app-routing.module';

import { TreeModule } from '@circlon/angular-tree-component';
import {AppComponent} from './app.component';
import {HeaderComponent} from './components/header.component';
import {FooterComponent} from './components/footer.component';
import {LoginComponent} from './components/login.component';
import {PageNotFoundComponent} from './components/page-not-found.component';
import {LicenseNotValidComponent} from "./components/license-not-valid.component";
import {HomeComponent} from './components/home.component';
import {UserComponent} from './components/users/user.component';
import {UserDialogComponent} from './components/user-dialog.component';
import {CustomFieldsComponent} from './components/customFields/customFields.component';
import {EntityFieldComponent} from './components/customFields/entity-field.component';
import {InfrastructureComponent} from './components/infrastructure/infrastructure.component';
import {InfrastructureHeaderComponent} from './components/infrastructure/infrastructure-header.component';
import {AssetsComponent} from './components/infrastructure/assets.component';
import {PeopleComponent} from './components/infrastructure/people.component';
import {ServerObjectsComponent} from './components/infrastructure/server-objects.component';
import {DevicesComponent} from './components/infrastructure/devices.component';
import {DriverObjectsComponent} from './components/infrastructure/driver-objects.component';
import {ServicesComponent} from './components/infrastructure/services.component';
import {FilterBarComponent} from './components/shared/filter-bar/filter-bar.component';
import {ReadersComponent} from './components/infrastructure/readers.component';
import {AntennasComponent} from './components/infrastructure/antennas.component';

import {TagsComponent} from './components/infrastructure/tags.component';
import {LocationsComponent} from './components/infrastructure/locations.component';
import {ZonesComponent} from './components/infrastructure/zones.component';
import {ConfirmDeleteDialogComponent} from './components/confirm-delete-dialog.component';
import {ShowGroupDialogComponent} from './components/show-group-dialog.component';
import {ManageGroupsDialogComponent} from './components/manage-groups-dialog.component';
import {AddToGroupDialogComponent} from './components/add-to-group-dialog.component';
import {RemoveFromGroupDialogComponent} from './components/remove-from-group-dialog.component';
import {SettingsComponent} from "./components/settings/settings.component";
import {SettingsEmailComponent} from "./components/settings/settings.email.component";
import {SettingsPasswordComponent} from "./components/settings/settings.password.component";
import {SettingsSessionComponent} from "./components/settings/settings.session.component";
import {SettingsUserComponent} from "./components/settings/settings.user.component";
import {SettingsLicensingComponent} from "./components/settings/settings.licensing.component";
import {SettingsMappingComponent} from "./components/settings/settings.mapping.component";
import {LicensingComponent} from "./components/licensing/licensing.component";
import {LocateComponent} from "./components/locate/locate.component";
import {AssetPopupDialogComponent} from "./components/locate/asset-popup-dialog.component";
import {PersonPopupDialogComponent} from "./components/locate/person-popup-dialog.component";
import {SecurityComponent} from "./components/security/security.component";
import {EntityInstanceDialogComponent} from "./components/infrastructure/dialogs/entity-instance-dialog.component";
import {EntityTypeDialogComponent} from './components/infrastructure/dialogs/entity-type-dialog.component';
import {InstanceFunctionComponent} from './components/infrastructure/dialogs/instance-function.component';
import {FunctionExecutingDialogComponent} from "./components/infrastructure/dialogs/function-executing-dialog.component";
import {ActionsComponent} from './components/actions.component';
import {ActionDialogComponent} from './components/action-dialog.component';
import {EventsComponent} from './components/events.component';
import {EventDialogComponent} from './components/event-dialog.component';
import {EventCreationDialogComponent} from './components/event-creation-dialog.component';
import {RfidEncodingSchemesComponent} from './components/infrastructure/rfid-encoding-schemes.component';
import { InstanceFunctionDialogComponent } from './components/locate/instance-function-dialog/instance-function-dialog.component';
import { InstanceDataDialogComponent } from './components/locate/instance-data-dialog/instance-data-dialog.component';
import { AbleNetShipmentValidationEnterInfoComponent } from './components/ablenet/shipment-validation-portal/enter-info/ablenet.shipment-validation.enter-info.component';
import { AbleNetShipmentValidationDashboardComponent } from './components/ablenet/shipment-validation-portal/dashboard/ablenet.shipment-validation.dashboard.component';
import { RestService } from './services/rest-service';
import {DialogService} from './services/dialog.service';
import {TagService} from './services/tag.service';
import {AuthenticationService} from './services/authentication.service';
import {GroupService} from './services/group.service';
import {UserGroupService} from "./services/user-group.service";
import {ImageService} from './services/image.service';
import {UserService} from './services/user.service';
import {UserDialogService} from './services/user-dialog.service';
import {ObjectService} from './services/object.service';
import {ServerObjectService} from './services/server-object.service';
import {DriverObjectService} from './services/driver-object.service';
import {ServiceService} from './services/service.service';
import {ReaderService} from './services/reader.service';
import {AntennaService} from './services/antenna.service';
import {LicensingService} from "./services/licensing.service";
import {LocationService} from './services/location.service';
import {ZoneService} from './services/zone.service';
import {PropertyTypeService} from './services/property-type.service';
import {WebSocketService} from "./services/websocket.service";
import {DialogWebSocketService} from "./services/dialog-web-socket.service";
import {SitesComponent} from "./components/sites/sites.component";
import {ActionService} from './services/action.service';
import {EventService} from './services/event.service';
import {EntityService} from './services/entity.service';
import {WorkflowService} from './services/workflow.service';
import {PingService} from "./services/ping.service";
import {PingServerService} from "./services/ping-server.service";

import {CanActivateAuthGuard} from './can-activate.authguard';
import {ForgotUsernamePasswordDialogComponent} from "./components/login/forgot-username-password-dialog.component";
import {MainAssetsComponent} from "./components/main-assets.component";
import {MainPeopleComponent} from "./components/main-people.component";

import {DraggableDirective} from './directives/draggable.directive';
import {PasswordResetService} from "./services/password-reset.service";

import {CapitalizePipe} from "./pipes/capitalize.pipe";
import {AlertDialogComponent} from "./components/alertDialog/alert-dialog.component";
import {MapComponent} from "./components/map/map.component";
import {CreateSiteDialogComponent} from "./components/sites/create-site-dialog.component";
import {SetScaleFinishDialogComponent} from "./components/sites/set-scale-finish-dialog.component";
import {SetScaleStartDialogComponent} from "./components/sites/set-scale-start-dialog.component";
import {SitesFooterComponent} from "./components/sites/sites-footer.component";
import {RoleManagerComponent} from "./components/roleManager/rolemanager.component";
import {RoleManagerDialogComponent} from "./components/roleManager/rolemanager-dialog.component";
import {LocationChangesDialogComponent} from "./components/sites/location-changes-dialog.component";
import {UploadingDialogComponent} from "./components/sites/uploading-dialog.component";
import {CanDeactivateGuard} from "./services/can-deactivate-guard.service";
import {LocationWebSocketService} from "./services/location-web-socket.service";
import {PagePermissionService} from "./services/page-permission.service";
import {HistoryComponent} from "./components/history/history.component";
import {HistoryDialogComponent} from "./components/history/history-dialog.component";
import {HistoryErrorDialogComponent} from "./components/history/history-error-dialog.component";
import {EmailAddressEditorComponent} from "./components/rules/actionEditors/dataEditors/email-address-editor.component";
import {PhoneNumberEditorComponent} from "./components/rules/actionEditors/dataEditors/phone-number-editor.component";
import {UserGroupSelectorComponent} from "./components/rules/actionEditors/dataEditors/user-group-selector.component";
import {UserSelectorComponent} from "./components/rules/actionEditors/dataEditors/user-selector.component";
import {DefaultActionEditorComponent} from "./components/rules/actionEditors/default-action-editor.component";
import {DialogActionEditorComponent} from "./components/rules/actionEditors/dialog-action-editor.component";
import {DialogButtonsComponent} from "./components/rules/actionEditors/dialog/dialog-buttons.component";
import {DialogDisplayComponent} from "./components/rules/actionEditors/dialog/dialog-display.component";
import {DialogMessageEditorComponent} from "./components/rules/actionEditors/dialog/dialog-message-editor.component";
import {DialogRecipientsComponent} from "./components/rules/actionEditors/dialog/dialog-recipients.component";
import {DialogResponseTimeComponent} from "./components/rules/actionEditors/dialog/dialog-response-time.component";
import {EmailActionEditorComponent} from "./components/rules/actionEditors/email-action-editor.component";
import {SmsActionEditorComponent} from "./components/rules/actionEditors/sms-action-editor.component";
import { DayParameterEditorComponent } from "./components/rules/parameterEditors/day-parameter-editor.component";
import { TimeParameterEditorComponent } from "./components/rules/parameterEditors/time-parameter-editor.component";
import { DialogParameterEditorComponent } from "./components/rules/parameterEditors/dialog-parameter-editor.component";
import { EntityParameterEditorComponent } from "./components/rules/parameterEditors/entity-parameter-editor.component";
import { ObjectParameterEditorComponent } from "./components/rules/parameterEditors/object-parameter-editor.component";
import {TagParameterEditorComponent} from "./components/rules/parameterEditors/tag-parameter-editor.component";
import {LocationFilterEditorComponent} from "./components/rules/filterEditors/location-filter-editor.component";
import {TimeFilterEditorComponent} from "./components/rules/filterEditors/time-filter-editor.component";
import {RuleParameterEditorComponent} from "./components/rules/parameterEditors/rule-parameter-editor.component";
import { FunctionParameterEditorComponent } from "./components/rules/parameterEditors/function-parameter-editor.component";
import {RuleEditorActionEditorComponent} from "./components/rules/rule-editor-action-editor.component";
import {RuleEditorLhsNavigationComponent} from "./components/rules/rule-editor-lhs-navigation.component";
import {RuleEditorStepAddFiltersComponent} from "./components/rules/rule-editor-step-add-filters.component";
import {RuleEditorStepChooseReactionsComponent} from "./components/rules/rule-editor-step-choose-reactions.component";
import {RuleEditorStepReviewAndFinishComponent} from "./components/rules/rule-editor-step-review-and-finish.component";
import {RuleEditorStepSelectTriggerComponent} from "./components/rules/rule-editor-step-select-trigger.component";
import {RuleEditorStepSetupParametersComponent} from "./components/rules/rule-editor-step-setup-parameters.component";
import {RuleEditorComponent} from "./components/rules/rule-editor.component";
import {RulesComponent} from "./components/rules/rules.component";
import {GtinValidationSetComponent} from "./components/gtin-validation-set/gtin-validation-set.component";
import {GtinValidationSetService} from "./services/gtin-validation-set.service";
import {DialogNotificationService} from "./services/dialog-notification.service";
import {ShellComponent} from "./components/shell.component";
import {ContentEditableDirective, Keyword} from "./directives/content-editable.directive";
import {RoleNotificationService} from "./services/role-notification.service";
import {RulesService} from "./services/rules.service";
import {HistoryService} from "./services/history.service";
import {KeywordService} from "./services/keyword.service";
import {StartupService} from "./services/startup.service";
import {EntryExitWebSocketService} from "./services/entry-exit-web-socket.service";
import {SessionExpirationDialogComponent} from "./components/session-expiration-dialog.component";
import { ViewLicenseDialogComponent } from './components/licensing/view-license-dialog.component';
import { ViewCompiledLicenseDialogComponent } from './components/licensing/view-compiled-license-dialog.component';
import { EntityFilterEditorComponent } from './components/rules/filterEditors/entity-filter-editor.component';

import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { OwlDateTimeModule, OwlNativeDateTimeModule } from "ng-pick-datetime";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import { A11yModule } from '@angular/cdk/a11y';
import { AngularMultiSelectModule } from "angular2-multiselect-dropdown";
import { SitesHeaderNavComponent } from './components/sites/sites-header-nav.component';
import { ArchwizardModule } from 'angular-archwizard';
import { NgxSpinnerModule} from "ngx-spinner";
import { WorkflowsComponent } from './components/workflow/workflows.component';
import { WorkflowComponent } from './components/workflow/workflow.component';
import { WorkflowServiceNodeComponent } from './components/workflow/nodes/workflow-service-node.component';
import { WorkflowLogNodeComponent } from './components/workflow/nodes/workflow-log-node.component';
import { WorkflowLoopNodeComponent } from './components/workflow/nodes/workflow-loop-node.component';
import { HealthMonitorComponent} from "./components/healthMonitor/health-monitor.component";
import {HealthMonitorService} from "./services/health-monitor.service";
import {SetZCoordinateDialogComponent} from "./components/sites/set-z-coordinate-dialog.component";
import { FunctionActionEditorComponent } from './components/rules/actionEditors/function-action-editor.component';
import { SimpleFunctionActionEditorComponent } from './components/rules/actionEditors/simple-function-action-editor.component';
import { StringFunctionParameterEditorComponent } from './components/rules/actionEditors/functionParameterEditors/string-function-parameter-editor.component';
import { UserFunctionParameterEditorComponent } from './components/rules/actionEditors/functionParameterEditors/user-function-parameter-editor.component';
import { NumberFunctionParameterEditorComponent } from './components/rules/actionEditors/functionParameterEditors/number-function-parameter-editor.component';
import { ObjectFunctionParameterEditorComponent } from './components/rules/actionEditors/functionParameterEditors/object-function-parameter-editor.component';
import { BooleanFunctionParameterEditorComponent } from './components/rules/actionEditors/functionParameterEditors/boolean-function-parameter-editor.component';
import { LocationFunctionParameterEditorComponent } from './components/rules/actionEditors/functionParameterEditors/location-function-parameter-editor.component';
import { TagFunctionParameterEditorComponent } from './components/rules/actionEditors/functionParameterEditors/tag-function-parameter-editor.component';
import { ZoneFunctionParameterEditorComponent } from './components/rules/actionEditors/functionParameterEditors/zone-function-parameter-editor.component';
import { AntennaFunctionParameterEditorComponent } from './components/rules/actionEditors/functionParameterEditors/antenna-function-parameter-editor.component';
import { ReaderFunctionParameterEditorComponent } from './components/rules/actionEditors/functionParameterEditors/reader-function-parameter-editor.component';
import { DriverObjectFunctionParameterEditorComponent } from './components/rules/actionEditors/functionParameterEditors/driver-object-function-parameter-editor.component';
import { EventFunctionParameterEditorComponent } from './components/rules/actionEditors/functionParameterEditors/event-function-parameter-editor.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import {NgxPaginationModule} from "ngx-pagination";
import {FilterPopupDialogComponent} from "./components/locate/filter-popup-dialog.component";
import {CookieService} from "ngx-cookie-service";
import { IconUploadConfirmDialog } from './components/icon-upload-confirm-dialog.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { MonitorZoneComponent } from './components/locate/monitor-zone/monitor-zone.component';
import { NewMapComponent } from './components/map/new-map.component';
import { ServerSettingsService } from './services/server-settings.service';
import { MonitorZoneWebSocketService } from './services/monitor-zone-web-socket-service';
import { ObjectInstanceWebSocketService } from './services/object-instance-websocket.service';
import { InstancePopupDialogComponent } from './components/locate/instance-popup-dialog/instance-popup-dialog.component';
import { MonitorAssetsComponent } from './components/locate/monitor-assets/monitor-assets.component';
import { MonitorAssets_SelectedAssetWebSocketService } from './services/monitor-assets-selected-asset-web-socket-service';
import { SaveAssetListDialogComponent } from './components/locate/monitor-assets/save-asset-list-dialog/save-asset-list-dialog.component';
import { MonitorLocationComponent } from './components/locate/monitor-location/monitor-location.component';
import { MonitorLocationWebSocketService } from './services/monitor-location-web-socket.service';
import { SimpleConfirmDialogComponent } from './components/simple-confirm-dialog/simple-confirm-dialog.component';
import { PropertyDataInfrastructureComponent } from './components/infrastructure/dialogs/property-data-infrastructure.component';
import { MonitorTagsComponent } from './components/locate/monitor-tags/monitor-tags.component';
import { SaveTagListDialogComponent } from './components/locate/monitor-tags/save-tag-list-dialog/save-tag-list-dialog.component';
import { MonitorTags_SelectedTagWebSocketService } from './services/monitor-tags-selected-tag-web-socket-service';
import { TagPopupDialogComponent } from './components/locate/tag-popup-dialog/tag-popup-dialog.component';
import { KissPrintPageComponent } from './components/kiss/print/kiss-print-page.component';
import { KissService } from './services/kiss.service';
import { KissPortalValidationEnterInfoComponent } from './components/kiss/portal_validation/enter-info/kiss-portal-validation.enter-info.component';
import { KissPortalValidationSelectPortalComponent } from './components/kiss/portal_validation/select-portal/kiss-portal-validation.select-portal.component';
import { KissPortalValidationDashboardComponent } from './components/kiss/portal_validation/dashboard/kiss-portal-validation.dashboard.component';
import { UsersMmcInfoComponent } from './components/mmc/users-mmc-info.component';
import { MmcService } from './services/mmc.service';
import { SetAffiliateDialogComponent } from './components/mmc/set-affiliate-dialog/set-affiliate-dialog.component';																						   
import { SimpleIndefiniteProgressDialogComponent } from './components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component';
import { SystemSettingsService } from './services/system-settings.service';
import { DemoOrderValidationService } from './services/demo-order-validation.service';
import { DemoOrderValidationSelectPortalComponent } from './components/demo-order-validation/select-portal/demo-order-validation.select-portal.component';
import { DemoOrderValidationEnterInfoComponent } from './components/demo-order-validation/enter-info/demo-order-validation.enter-info.component';
import { DemoOrderValidationDashboardComponent } from './components/demo-order-validation/dashboard/demo-order-validation.dashboard.component';
import { DropdownMenuComponent } from './components/infrastructure/dropdown-menu/dropdown-menu.component';
import { GeneralFunctionDialogComponent } from './components/infrastructure/dialogs/general-function-dialog.component';
import { DenverMattressPortalValidationSelectPortalComponent } from './components/denverMattress/portal_validation/select-portal/dm-portal-validation.select-portal.component';																																					
import { DenverMattressService } from './services/denver-mattress.service';
import { DenverMattressPortalValidationDashboardComponent } from './components/denverMattress/portal_validation/dashboard/dm-portal-validation.dashboard.component';
import { HHSeekAndFindPageComponent } from './components/hhseekandfind/page/hhseekandfind-page.component';
import { HHSeekAndFindService } from './services/hhseekandfind.service';
import { HHSeekAndFindDialogComponent } from './components/hhseekandfind/dialog/hhseekandfind-dialog.component';
import { BuchananProductUploadComponent } from './components/buchanan/product-upload/buchanan.product-upload.component';
import { BuchananService } from './services/buchanan.service';
import { BuchananBulkPrintComponent } from './components/buchanan/bulk-print/buchanan.bulk-print.component';
import { BuchananPrintComponent } from './components/buchanan/print/buchanan.print.component';
import { OneillService } from './services/oneill.service';
import { OneilPortalValidationSelectPortalComponent } from './components/oneil/select-portal/oneil-portal-validation.select-portal.component';
import { OneilPortalValidationDashboardComponent } from './components/oneil/dashboard/oneil-portal-validation.dashboard.component';
import { UserPagePermission } from './services/user-page-permission.service';
import { DialogsComponent } from './components/dialogs/dialogs.component';
import { LoadGtinValidationSetDialogComponent } from "./components/gtin-validation-set/load-gtin-validation-set.dialog.component";
import { ViewGtinValidationSetDialogComponent } from "./components/gtin-validation-set/view-gtin-validation-set.dialog.component";
import { EditViewGtinValidationSetDialogComponent } from "./components/gtin-validation-set/edit-view-gtin-validation-set.dialog.component";
import { SimpleSuccessErrorDialogComponent } from './components/simple-success-error-dialog-box/simple-success-error-dialog-box.component';
import { ApiTokenComponent } from "./components/apiToken/apiToken.component";
import { ApiTokenService } from "./services/apitoken.service";
import { CartonAuditSelectLocationComponent } from './components/jockey/carton-audit/select-location/carton-audit-select-location.component';
import { CartonAuditService } from './services/carton-audit.service';
import { CartonAuditEnterInfoComponent } from "./components/jockey/carton-audit/enter-info/carton-audit-enter-info.component";
import { CartonAuditDashboardComponent } from "./components/jockey/carton-audit/dashboard/carton-audit-dashboard.component";
import {MultiFactorAuthenticationComponent} from './components/multifactor-authentication.component';
import { AblenetService } from './services/ablenet.service';
import { AbleNetShipmentValidationSelectPortalComponent } from './components/ablenet/shipment-validation-portal/select-portal/ablenet.shipment-validation.select-portal.component';
import { UserValidationDialogComponent } from './components/login-validation-dialog/user-validation-dialog.component';
import { DataMappingHdrComponent } from "./components/dataMapping/data-mapping-hdr.component";
import { DataMappingViewDialogComponent } from "./components/dataMapping/data-mapping-view.dialog.component";
import { DataMappingService } from './services/data-mapping.service';
import { DataMappingUploadService } from './services/data-mapping-upload.service';
import { DataMappingDtlDialogComponent } from "./components/dataMapping/data-mapping-dtl.dialogs.component";
import { DataMappingUploadComponent} from "./components/dataMapping/data-mapping-upload.component";
import { KissOutboundValidationSelectPortalComponent } from './components/kiss/outbound/select-portal/kiss-outbound-validation.select-portal.component';
import { KissOutboundValidationEnterInfoComponent } from './components/kiss/outbound/enter-info/kiss-outbound-validation.enter-info.component';
import { KissOutboundValidationDashboardComponent } from './components/kiss/outbound/dashboard/kiss-outbound-validation.dashboard.component';
import { AbleNetReturnDevicesSelectPortalComponent } from './components/ablenet/return-devices-portal/select-portal/ablenet.return-devices.select-portal.component';
import { AblenetReturnDevicesValidationService } from './services/ablenet.returndevices.service';
import { AbleNetReturnDevicesDashboardComponent } from './components/ablenet/return-devices-portal/dashboard/ablenet.return-devices.dashboard.component';
import { ZoneAssignedAssetsComponent } from './components/zone-assigned-assets/main-page/zone-assigned-assets.component';
import { ZoneAssignedAssetsService } from './services/zone-assigned-assets.service';
import { ZoneAssignedAssetsDialogComponent } from './components/zone-assigned-assets/dialog/zone-assigned-assets-dialog.component';
import { AddAssetDialogComponent } from './components/zone-assigned-assets/add-asset-dialog/add-asset-dialog.component';

@NgModule({
  declarations: [
    DraggableDirective,
    ContentEditableDirective,
    CapitalizePipe,

    AppComponent,
    HeaderComponent,
    FooterComponent,
    LoginComponent,
    PageNotFoundComponent,
    LicenseNotValidComponent,
    HomeComponent,
    UserComponent,
    UserDialogComponent,
    InfrastructureComponent,
    InfrastructureHeaderComponent,
    AssetsComponent,
    PeopleComponent,
    ServerObjectsComponent,
    DriverObjectsComponent,
    DevicesComponent,
    ServicesComponent,
    FilterBarComponent,
    ReadersComponent,
    TagsComponent,
    AntennasComponent,
    LocationsComponent,
    ZonesComponent,
    ConfirmDeleteDialogComponent,
    ShowGroupDialogComponent,
    ManageGroupsDialogComponent,
    AddToGroupDialogComponent,
    RemoveFromGroupDialogComponent,
    SettingsComponent,
    SettingsEmailComponent,
    SettingsPasswordComponent,
    SettingsSessionComponent,
    SettingsUserComponent,
    SettingsLicensingComponent,
    SettingsMappingComponent,
    EntityTypeDialogComponent,
    EntityInstanceDialogComponent,
    HealthMonitorComponent,
    InstanceFunctionComponent,
    FunctionExecutingDialogComponent,
    LicensingComponent,
    LocateComponent,
    PersonPopupDialogComponent,
    AssetPopupDialogComponent,
    MainAssetsComponent,
    MainPeopleComponent,
    ForgotUsernamePasswordDialogComponent,
    SecurityComponent,
    SitesHeaderNavComponent,
    SitesComponent,
    SitesFooterComponent,
    AlertDialogComponent,
    RoleManagerComponent,
    RoleManagerDialogComponent,
    SetScaleStartDialogComponent,
    SetScaleFinishDialogComponent,
    CreateSiteDialogComponent,
    UploadingDialogComponent,
    MapComponent,
    ActionsComponent,
    ActionDialogComponent,
    EventsComponent,
    EventDialogComponent,
    LocationChangesDialogComponent,
    EventCreationDialogComponent,
    HistoryComponent,
    HistoryDialogComponent,
    HistoryErrorDialogComponent,
    ShellComponent,
    RulesComponent,
    RuleEditorComponent,
    RuleEditorLhsNavigationComponent,
    RuleEditorStepSelectTriggerComponent,
    RuleEditorStepSetupParametersComponent,
    RuleEditorStepAddFiltersComponent,
    RuleEditorStepChooseReactionsComponent,
    RuleEditorStepReviewAndFinishComponent,
    RuleEditorActionEditorComponent,
    EntityFilterEditorComponent,
    SmsActionEditorComponent,
    EmailActionEditorComponent,
    DialogActionEditorComponent,
    FunctionActionEditorComponent,
    SimpleFunctionActionEditorComponent,
    StringFunctionParameterEditorComponent,
    UserFunctionParameterEditorComponent,
    NumberFunctionParameterEditorComponent,
    ObjectFunctionParameterEditorComponent,
    BooleanFunctionParameterEditorComponent,
    LocationFunctionParameterEditorComponent,
    TagFunctionParameterEditorComponent,
    ZoneFunctionParameterEditorComponent,
    ReaderFunctionParameterEditorComponent,
    AntennaFunctionParameterEditorComponent,
    DriverObjectFunctionParameterEditorComponent,
    EventFunctionParameterEditorComponent,
    DefaultActionEditorComponent,
    PhoneNumberEditorComponent,
    UserSelectorComponent,
    UserGroupSelectorComponent,
    EmailAddressEditorComponent,
    DialogRecipientsComponent,
    DialogButtonsComponent,
    DialogResponseTimeComponent,
    DialogDisplayComponent,
    DialogMessageEditorComponent,
    TagParameterEditorComponent,
    EntityParameterEditorComponent,
    ObjectParameterEditorComponent,
    FunctionParameterEditorComponent,
    SessionExpirationDialogComponent,
    RuleParameterEditorComponent,
    LocationFilterEditorComponent,
    TimeFilterEditorComponent,
    DialogParameterEditorComponent,
    CustomFieldsComponent,
    DayParameterEditorComponent,
    TimeParameterEditorComponent,
    EntityFieldComponent,
    ViewLicenseDialogComponent,
    ViewCompiledLicenseDialogComponent,
    RfidEncodingSchemesComponent,
    WorkflowsComponent,
    WorkflowComponent,
    WorkflowServiceNodeComponent,
    WorkflowLogNodeComponent,
    WorkflowLoopNodeComponent,
    SetZCoordinateDialogComponent,
    FilterPopupDialogComponent,
    IconUploadConfirmDialog,
    InstanceFunctionDialogComponent,
    InstanceDataDialogComponent,
    MonitorZoneComponent,
    NewMapComponent,
    InstancePopupDialogComponent,
    MonitorAssetsComponent,
    SaveAssetListDialogComponent,
    MonitorLocationComponent,
    SimpleConfirmDialogComponent,
    PropertyDataInfrastructureComponent,
    MonitorTagsComponent,
    SaveTagListDialogComponent,
    TagPopupDialogComponent,
    KissPrintPageComponent,
    KissPortalValidationEnterInfoComponent,
    KissPortalValidationSelectPortalComponent,
    KissPortalValidationDashboardComponent,
    KissOutboundValidationSelectPortalComponent,
    KissOutboundValidationEnterInfoComponent,
    KissOutboundValidationDashboardComponent,
    UsersMmcInfoComponent,
    SetAffiliateDialogComponent,
    SimpleIndefiniteProgressDialogComponent,
    DemoOrderValidationSelectPortalComponent,
    DemoOrderValidationEnterInfoComponent,
    DemoOrderValidationDashboardComponent,
    DropdownMenuComponent,
    GeneralFunctionDialogComponent,
    DenverMattressPortalValidationSelectPortalComponent,
    DenverMattressPortalValidationDashboardComponent,
    HHSeekAndFindPageComponent,
    HHSeekAndFindDialogComponent,
    BuchananProductUploadComponent,
    BuchananBulkPrintComponent,
    BuchananPrintComponent,
    OneilPortalValidationSelectPortalComponent,
    OneilPortalValidationDashboardComponent,
    DialogsComponent,
    GtinValidationSetComponent,
    LoadGtinValidationSetDialogComponent,
    ViewGtinValidationSetDialogComponent,
    EditViewGtinValidationSetDialogComponent,
    SimpleSuccessErrorDialogComponent,
    ApiTokenComponent,
    CartonAuditSelectLocationComponent,
    CartonAuditEnterInfoComponent,
    CartonAuditDashboardComponent,
    MultiFactorAuthenticationComponent,
    AbleNetShipmentValidationEnterInfoComponent,
    AbleNetShipmentValidationDashboardComponent,
    AbleNetShipmentValidationSelectPortalComponent,
    AbleNetReturnDevicesSelectPortalComponent,
    AbleNetReturnDevicesDashboardComponent,
    UserValidationDialogComponent,
    DataMappingHdrComponent,
    DataMappingViewDialogComponent,
    DataMappingDtlDialogComponent,
    DataMappingUploadComponent,
    ZoneAssignedAssetsComponent,
    ZoneAssignedAssetsDialogComponent,
    AddAssetDialogComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    HttpClientModule,
    AppRoutingModule,
    TypeaheadModule.forRoot(),
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    BrowserAnimationsModule,
    A11yModule,
    AngularMultiSelectModule,
    ArchwizardModule,
    NgxSpinnerModule,
    NgxPaginationModule,
    TooltipModule.forRoot(),
    TreeModule,
    NgbModule
  ],
  providers: [
    RestService,
			   
    HealthMonitorService,
    PagePermissionService,
    WebSocketService,
    DialogWebSocketService,
    AuthenticationService,
    LicensingService,
    CookieService,

    StartupService,
    {
      provide: APP_INITIALIZER,
      useFactory: (startupService: StartupService) => function(): Promise<void> {return startupService.initialize()},
      deps: [StartupService],
      multi: true
    },

    DialogService,
    TagService,
    GroupService,
    UserGroupService,
    ImageService,
    UserService,
    UserDialogService,
    ObjectService,
    ServerObjectService,
    DriverObjectService,
    ServiceService,
    ReaderService,
    AntennaService,
    LocationService,
    ZoneService,
    PropertyTypeService,
    PasswordResetService,
    ActionService,
    EventService,
    LocationWebSocketService,
    RulesService,
     GtinValidationSetService,
    HistoryService,
    DialogNotificationService,
    RoleNotificationService,
    KeywordService,
    EntityService,
    CanActivateAuthGuard,
    CanDeactivateGuard,
    WorkflowService,
    PingService,
    PingServerService,
    EntryExitWebSocketService,
    ServerSettingsService,
    MonitorZoneWebSocketService,
    ObjectInstanceWebSocketService,
    MonitorAssets_SelectedAssetWebSocketService,
    MonitorTags_SelectedTagWebSocketService,
    MonitorLocationWebSocketService,
    SystemSettingsService,
    KissService,
    MmcService,
    DemoOrderValidationService,
    DenverMattressService,
    BuchananService,
    OneillService,
    UserPagePermission,
    HHSeekAndFindService,
    BuchananService,
    ApiTokenService,
    CartonAuditService,
    AblenetService,
    DataMappingService,
    DataMappingUploadService,
    AblenetReturnDevicesValidationService,
    ZoneAssignedAssetsService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}